import { useEffect, useRef } from "react";
import aboutVideo from "../assets/gallery/newaddpicbessandsolarpic/about.mp4";
import "../styles/About.scss";

const About = () => {
  const canvasRef = useRef(null);
  const valuesRef = useRef(null);

  // Enhanced intersection observer for values section
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('.value-card');
            cards.forEach((card, index) => {
              setTimeout(() => {
                card.classList.add('animate-in');
              }, index * 150); // Staggered animation
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    if (valuesRef.current) {
      observer.observe(valuesRef.current);
    }

    return () => {
      if (valuesRef.current) {
        observer.unobserve(valuesRef.current);
      }
    };
  }, []);

  // Simplified particle animation with better performance
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const particles = [];
    const particleCount = 40; // Reduced for better performance

    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 2 + 1,
        speedX: (Math.random() - 0.5) * 0.3, // Slower animation
        speedY: (Math.random() - 0.5) * 0.3,
        color: Math.random() > 0.5 ? "#0047AB" : "#87CEEB",
        opacity: Math.random() * 0.3 + 0.1, // More subtle
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Simplified gradient
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, "rgba(0, 71, 171, 0.02)");
      gradient.addColorStop(1, "rgba(255,255,255,0.02)");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p) => {
        p.x += p.speedX;
        p.y += p.speedY;
        if (p.x < 0 || p.x > canvas.width) p.speedX = -p.speedX;
        if (p.y < 0 || p.y > canvas.height) p.speedY = -p.speedY;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `${p.color}${Math.floor(p.opacity * 255).toString(16)}`;
        ctx.fill();
      });

      // Reduced connections for better performance
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 100) { // Reduced distance
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(0, 71, 171, ${(1 - distance / 100) * 0.05})`; // More subtle
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      requestAnimationFrame(animate);
    };

    animate();
    return () => window.removeEventListener("resize", resizeCanvas);
  }, []);

  const companyValues = [
    {
      title: "STEWARDSHIP",
      content: "We act as responsible custodians of the environment and resources, advancing clean energy with a long-term commitment to sustainability and nation-building.",
      icon: "🌱"
    },
    {
      title: "INGENUITY",
      content: "We apply foresight, innovation, and engineering discipline to create resilient, future-ready solar and energy storage solutions.",
      icon: "💡"
    },
    {
      title: "EXCELLENCE",
      content: "We pursue uncompromising standards of quality, safety, and performance across EPC execution, manufacturing, and project delivery.",
      icon: "⭐"
    },
    {
      title: "INTEGRITY",
      content: "We operate with honesty, openness, and accountability—ensuring transparent governance, ethical conduct, and regulatory compliance in every engagement.",
      icon: "🛡️"
    },
    {
      title: "COLLECTIVE",
      content: "We believe enduring progress is powered by people, partnerships, and shared purpose, fostering collaboration and mutual growth.",
      icon: "🤝"
    },
  ];

  const timelineEvents = [
    {
      year: "DEC. 2017",
      events: [
        "DIVERSIFIED INTO SOLAR OPERATIONS IN INDIA",
        "EPC CONTRACT FOR 05+ MW PROJECT",
        "SECURED RENOWNED EPC PROJECTS OF CAPACITY 01 MW FROM MES AIR FORCE STATION BARMER, RAJASTHAN"
      ],
      icon: "🚀"
    },
    {
      year: "DEC. 2019",
      events: [
        "SUPPLIED 10 MW SOLAR MODULE AND 05 MW SOLAR GRID TIE INVERTER IN GUJARAT, RAJASTHAN & MAHARASHTRA",
        "SECURED EPC PROJECTS OF CAPACITY 01 MW FROM MES AIR FORCE STATION PHALODI, RAJASTHAN",
        "MANPOWER INCREASED FROM 25 TO 50"
      ],
      icon: "📈"
    },
    {
      year: "DEC. 2021",
      events: [
        "10 MW SOLAR I&C WORK IN RAJ.",
        "10 MW SOLAR MODULE AND 05 MW SOLAR INVERTER SUPPLIED IN PAN INDIA",
        "SECURED EPC PROJECTS OF CAPACITY 01 MW FROM MILITARY ENGINEERING SERVICES AIR FORCE STATION BARMER RAJASTHAN",
        "COMPLETED 15+ EPC PROJECTS"
      ],
      icon: "⚡"
    },
    {
      year: "DEC. 2022",
      events: [
        "50 MW SOLAR I&C WORK IN RAJASTHAN",
        "SOLAR POWER PLANTS INSTALLED OF 50 MW IN KUSUM'S AND C&I SEGMENTS.",
        "2 MW WORK EXECUTED AT MILITARY STATION KACHRAPAR A AND KAKINARA.",
        "300+ PLUS HOUSES SOLARIZED UNDER ROOF-TOP SUBSIDY SCHEMES"
      ],
      icon: "🏆"
    },
    {
      year: "DEC. 2023",
      events: [
        "100 MW MODULES SUPPLIED IN PAN INDIA",
        "70MW SOLAR PROJECT COMMISSIONED UNDER KUSUM A COMPONENT.",
        "SECURED 100 MW LOA UNDER KUSUM C COMPONENT IN RAJASTHAN",
        "INCREASED MANPOWER STRENGTH FROM 50 TO 200",
        "PUMPED THE FINANCIAL EXPOSURES BY 200+ CR.SET THE BENCHMARK OF ADANI SOLAR IN WESTERN RAJASTHAN"
      ],
      icon: "🌟"
    },
    {
      year: "DEC. 2024",
      events: [
        "200 MW MODULES SUPPLIED IN PAN INDIA",
        "110 MW SOLAR PROJECTS COMMISSIONED UNDER KUSUM A COMPONENT.",
        "300 MW SOLAR PROJECTS COMMISSIONED UNDER KUSUM C COMPONENT",
        "INCREASED MANPOWER STRENGTH FROM 200 TO 300",
        "PUMPED THE FINANCIAL EXPOSURES BY 400+ CR"
      ],
      icon: "🚀"
    },
    {
      year: "Nov. 2025",
      events: [
        "250 MW MODULES SUPPLIED IN PAN INDIA",
        "500 MW SOLAR PROJECTS COMMISSIONED UNDER KUSUM C COMPONENT.",
        "INCREASED MANPOWER STRENGTH FROM 300 TO 500",
        "PUMPED THE FINANCIAL EXPOSURES BY 1,000+ CR. SET THE BENCHMARK OF ADANI SOLAR IN ALL RAJASTHAN",
        "DEVELOPED 500+ MW SOLAR IPP DEVELOPERS",
        "SECURED 1 GWH+ BESS IPP ORDERS"
      ],
      icon: "🎯"
    }
  ];

  return (
    <>
      <section className="about section section-padding company-values" id="about">
        {/* Background elements */}
        <canvas ref={canvasRef} className="video-canvas"></canvas>

        {/* Main content */}
        <div className="about-container">
          <header className="section-header">
            <h1 className="section-title">About Rama Reflection India Pvt Ltd</h1>
            <p className="section-subtitle" style={{ color: "black" }}>
              Leading India's renewable energy revolution with cutting-edge solar EPC and BESS solutions since 2017
            </p>
          </header>
          <div className="about-content">
            <div className="about-text">
              <h3>Our Vision</h3>
              <p>To emerge as a trusted and recognized leader in clean and
                renewable energy, shaping a resilient and sustainable future
                for India. We envision powering communities and industries
                through integrated solar and energy storage solutions that
                deliver reliable performance, strengthen energy security, and
                accelerate the nation's transition toward a cleaner, smarter,
                and self-reliant energy ecosystem.
              </p>
              <h3>Our Mission</h3>
              <p>To design, manufacture, and deliver high-quality clean energy
                solutions with a strong focus on solar power and advanced
                energy storage systems. Through engineering excellence,
                transparent practices, and timely execution, we are
                committed to building reliable, future-ready infrastructure
                that consistently meets customer expectations and
                contributes meaningfully to India's renewable energy growth.</p>
            </div>
            <div className="about-image">
              <video autoPlay muted loop playsInline aria-label="Rama Reflection India renewable energy projects">
                <source src={aboutVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>

          <section className="" aria-labelledby="values-heading" ref={valuesRef}>
            <h2 id="values-heading" className="section-heading">Our Core Values</h2>
            <div className="row">
              <div className="col-12">
                <div className="row g-2 justify-content-center">
                  {companyValues.map((value, index) => (
                    <div className="col-lg-4 col-md-6 col-12 mb-md-0 mb-3" key={index}>
                      <div
                        className="value-card h-100"
                        role="article"
                        aria-labelledby={`value-title-${index}`}
                      >
                        <div className="value-icon" aria-hidden="true">
                          {value.icon}
                        </div>
                        <h3
                          className="value-title"
                          id={`value-title-${index}`}
                        >
                          {value.title}
                        </h3>
                        <p className="value-content">
                          {value.content}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="professional-timeline" aria-labelledby="timeline-heading">
            <div className="timeline-header">
              <h2 id="timeline-heading" className="timeline-title">Our Journey</h2>
              <p className="timeline-subtitle">Milestones of Excellence in Solar Energy</p>
            </div>
            <div className="timeline-container">
              <div className="timeline-track"></div>

              {timelineEvents.map((event, i) => (
                <div
                  key={i}
                  className={`timeline-milestone ${i % 2 === 0 ? 'milestone-left' : 'milestone-right'}`}
                  ref={el => {
                    if (el) {
                      const observer = new IntersectionObserver(
                        ([entry]) => {
                          if (entry.isIntersecting) {
                            setTimeout(() => {
                              el.classList.add('milestone-animate');
                            }, i * 150);
                          }
                        },
                        { threshold: 0.2 }
                      );
                      observer.observe(el);
                      return () => observer.unobserve(el);
                    }
                  }}
                >
                  <div className="milestone-marker">
                    <div className="marker-ring"></div>
                    <div className="marker-content">
                      <span className="marker-icon">{event.icon}</span>
                    </div>
                  </div>

                  <div className="milestone-card">
                    <div className="card-header">
                      <div className="year-badge">{event.year}</div>
                    </div>
                    <div className="card-content">
                      <div className="achievements-list">
                        {event.events.map((achievement, j) => (
                          <div key={j} className="achievement-item">
                            <div className="achievement-dot"></div>
                            <p className="achievement-text">{achievement}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </section>
    </>
  );
};

export default About;